import api from '../../../services/api';

/**
 * CLIENTE HTTP DA FÁBRICA (M6) — `/warehouse/{machines, production-orders,
 * production-lots, boms, kit-options}`.
 *
 * ── Por que este arquivo existe ─────────────────────────────────────────────
 * `services/api.js` ainda não tem métodos nomeados para o M6 (é arquivo do
 * dono). Em vez de espalhar `api.get('/warehouse/production-orders?...')` por
 * cinco telas — o jeito clássico de a mesma rota nascer escrita de dois modos
 * diferentes —, os endpoints ficam nomeados aqui, UMA vez.
 *
 * ⚠️ TRANSPORTE ÚNICO: tudo passa por `api.*`, que é o `fetchAPI` do projeto —
 * token, refresh proativo/reativo, `x-request-id` e a sonda de "backend fora do
 * ar" (BACKEND_OFFLINE_MESSAGE). Não existe `fetch` cru neste módulo, e nenhuma
 * função aqui inventa comportamento: cada uma é o par (método, rota) do
 * contrato do backend, nada mais. Quando os métodos entrarem no `api.js`, este
 * arquivo vira um repasse de uma linha cada.
 *
 * Wire snake_case, sem fallback duplo (CLAUDE.md §2).
 */

/** Querystring só com o que foi informado — `undefined`/`''` não viram filtro. */
function buildQuery(params = {}) {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value === null || value === undefined || value === '') {
            continue;
        }
        search.append(key, String(value));
    }
    const query = search.toString();
    return query ? `?${query}` : '';
}

/**
 * PATCH pelo MESMO transporte. `api.get(endpoint, options)` é o `fetchAPI` cru
 * (as options vão inteiras para o fetch), então o verbo é escolhido aqui sem
 * abrir um segundo caminho de rede. `api.post`/`api.put` existem nomeados; o
 * PATCH não — daí esta linha, e só ela.
 */
function patchJson(endpoint, data) {
    return api.get(endpoint, { method: 'PATCH', body: JSON.stringify(data) });
}

// ── Máquinas (cadastro) ─────────────────────────────────────────────────────

export function ListMachines(params = {}) {
    return api.get(`/warehouse/machines${buildQuery(params)}`);
}

export function CreateMachine(data) {
    return api.post('/warehouse/machines', data);
}

export function UpdateMachine(id, patch) {
    return patchJson(`/warehouse/machines/${encodeURIComponent(id)}`, patch);
}

// ── Ordens de produção (OP) ─────────────────────────────────────────────────

export function ListProductionOrders(params = {}) {
    return api.get(`/warehouse/production-orders${buildQuery(params)}`);
}

/**
 * Ficha: OP + a entrada/saída da máquina onde está aberta + os pallets de
 * saída (OS de transporte e onde cada um está agora) + lotes + material.
 */
export function GetProductionOrder(id) {
    return api.get(`/warehouse/production-orders/${encodeURIComponent(id)}`);
}

export function CreateProductionOrder(data) {
    return api.post('/warehouse/production-orders', data);
}

/** O painel das OPs em aberto (topo da tabela do gestor). */
export function GetProductionOrdersSummary() {
    return api.get('/warehouse/production-orders/summary');
}

/** Os inventory_id do Full do produto de saída (automático — o formulário só mostra). */
export function GetKitInventoryIds(productOutId) {
    return api.get(`/warehouse/production-orders/kit-inventory-ids${buildQuery({ product_out_id: productOutId })}`);
}

export function UpdateProductionOrder(id, patch) {
    return patchJson(`/warehouse/production-orders/${encodeURIComponent(id)}`, patch);
}

// ── Apontamento da bancada (chão: wms:operate + crachá) ─────────────────────

/** Encerra a mãe anterior da máquina e abre a nova (§6.7). */
export function CaptureMachineMaterial(id, data) {
    return api.post(`/warehouse/production-orders/${encodeURIComponent(id)}/capture-material`, data);
}

/** "Fim de MP": encerra a associação e abre a OS REPLENISHMENT em BACKLOG. */
export function FinishMachineMaterial(id, data) {
    return api.post(`/warehouse/production-orders/${encodeURIComponent(id)}/finish-material`, data);
}

/** O bipe do lote do fornecedor: tira o item do LPN e vira WIP (regra 5-iii). */
export function ConsumeProductionMaterial(id, data) {
    return api.post(`/warehouse/production-orders/${encodeURIComponent(id)}/consume`, data);
}

/** Apontamento de produção: LPN de produto acabado na OT + etiqueta do kit. */
export function ProduceProductionOutput(id, data) {
    return api.post(`/warehouse/production-orders/${encodeURIComponent(id)}/produce`, data);
}

/**
 * Frente 3 — apontamento por BIPE do inventory_id (bancada sem lotes):
 * `{ operator_code, scan_method, inventory_id }`, onde `inventory_id` é o que o
 * leitor leu na etiqueta do Full do kit. Confere com o inventory_id da OP e
 * soma +1 kit no pallet que o operador bipou.
 */
export function AppointKitByInventoryId(id, data) {
    return api.post(`/warehouse/production-orders/${encodeURIComponent(id)}/appoint-kit`, data);
}

// A BANCADA abre, pausa e finaliza a OP na máquina do operador (a OP nasce sem
// máquina, como as OS). Corpo: `{ operator_code, machine_code }`.
/**
 * Máquina ↔ operador da bancada: a lista diz quem está em cada máquina (para
 * este `terminal_id`); assumir renova o sinal; liberar solta a máquina e pausa
 * a OP aberta nela. Corpo: `{ operator_code, terminal_id }`.
 */
export function ListBenchMachines(params = {}) {
    return api.get(`/warehouse/bench/machines${buildQuery(params)}`);
}

export function ClaimBenchMachine(code, data) {
    return api.post(`/warehouse/bench/machines/${encodeURIComponent(code)}/claim`, data);
}

export function ReleaseBenchMachine(code, data) {
    return api.post(`/warehouse/bench/machines/${encodeURIComponent(code)}/release`, data);
}

export function OpenProductionOrder(id, data) {
    return api.post(`/warehouse/production-orders/${encodeURIComponent(id)}/open`, data);
}

export function PauseProductionOrder(id, data) {
    return api.post(`/warehouse/production-orders/${encodeURIComponent(id)}/pause`, data);
}

export function FinishProductionOrder(id, data) {
    return api.post(`/warehouse/production-orders/${encodeURIComponent(id)}/finish`, data);
}

/**
 * O BIPE DO PALLET: o operador bipa um LPN em branco (o 1º bipe da OP). O
 * pallet atual encerra (com kits, ganha a OS de transporte) e o bipado nasce
 * na saída da máquina — ou em formação, se a saída ainda tem o anterior.
 * Corpo: `{ operator_code, machine_code, terminal_id, scan_method, lpn_code }`.
 */
export function ScanProductionOutputLpn(id, data) {
    return api.post(`/warehouse/production-orders/${encodeURIComponent(id)}/output-lpn`, data);
}

// ── Lotes de produção (LT) e genealogia ─────────────────────────────────────

/** `supplier_lot` no filtro é O RECALL: "quais LTs consumiram o lote X?". */
export function ListProductionLots(params = {}) {
    return api.get(`/warehouse/production-lots${buildQuery(params)}`);
}

/** Genealogia do LT: kit → LT → lotes de fornecedor → ordem de compra → FORNECEDOR. */
export function GetProductionLotGenealogy(id) {
    return api.get(`/warehouse/production-lots/${encodeURIComponent(id)}/genealogy`);
}

/**
 * O SENTIDO INVERSO — do lote do FORNECEDOR para a frente: de qual compra ele
 * entrou (pelos pallets que o trouxeram) e quais LTs/caixas saíram dele.
 *
 * O lote vai em QUERY porque é string livre do fornecedor (a etiqueta da
 * bobina) e pode ter barra, que quebraria o caminho da rota.
 */
export function GetSupplierLotGenealogy(supplierLot) {
    return api.get(`/warehouse/supplier-lots/genealogy${buildQuery({ supplier_lot: supplierLot })}`);
}

/**
 * REIMPRESSÃO da etiqueta do kit — a segunda via reconstruída do APONTAMENTO
 * gravado (produto + LT + quantidade), no mesmo motor ZPL do M1/M3.
 */
export function GetLotOutputKitLabel(outputId) {
    return api.get(`/warehouse/lot-outputs/${encodeURIComponent(outputId)}/kit-label`);
}

// ── Ficha técnica (BOM de rendimento) ───────────────────────────────────────

export function ListBoms(params = {}) {
    return api.get(`/warehouse/boms${buildQuery(params)}`);
}

export function CreateBom(data) {
    return api.post('/warehouse/boms', data);
}

export function UpdateBom(id, patch) {
    return patchJson(`/warehouse/boms/${encodeURIComponent(id)}`, patch);
}

// ── Opções de kit (composição flexível) ─────────────────────────────────────

export function ListKitOptions(params = {}) {
    return api.get(`/warehouse/kit-options${buildQuery(params)}`);
}

export function CreateKitOption(data) {
    return api.post('/warehouse/kit-options', data);
}

export function UpdateKitOption(id, patch) {
    return patchJson(`/warehouse/kit-options/${encodeURIComponent(id)}`, patch);
}
